import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  DollarSign,
  CheckCircle,
  XCircle,
  RefreshCw,
  Loader2,
  Users,
  Calendar,
  Wallet,
  Clock,
  Zap,
  AlertTriangle,
} from "lucide-react";
import payoutDashboardService from "../../lib/payoutDashboardService";
import Card from "../ui/Card";
import Alert from "../ui/Alert";
import SortIndicator from "../ui/SortIndicator";

// Helper function to format currency
const formatCurrency = (amount) => {
  const num = parseFloat(amount) || 0;
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(num);
};

// Helper function to format date
const formatDate = (dateString) => {
  if (!dateString) return "-";
  const date = new Date(dateString);
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

const PayoutDashboard = () => {
  // State
  const [dashboardData, setDashboardData] = useState([]);
  const [weekStatus, setWeekStatus] = useState([]);
  const [eligibleAffiliates, setEligibleAffiliates] = useState([]);
  const [failedPayouts, setFailedPayouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sortConfig, setSortConfig] = useState({
    key: "unpaid_balance",
    direction: "desc",
  });

  // Load dashboard data
  const loadDashboardData = useCallback(async () => {
    setLoading(true);
    setError(null);

    // Fetch all data in parallel
    const [dashboardResult, weekStatusResult, eligibleResult, failedResult] = await Promise.all([
      payoutDashboardService.getPayoutDashboardData(),
      payoutDashboardService.getWeekFinalizationStatus(6),
      payoutDashboardService.getEligibleAffiliates(),
      payoutDashboardService.getFailedPayouts(),
    ]);

    if (dashboardResult.success) {
      setDashboardData(dashboardResult.data);
    } else {
      setError(dashboardResult.error || "Failed to load dashboard data");
    }

    if (weekStatusResult.success) {
      setWeekStatus(weekStatusResult.data);
    }

    if (eligibleResult.success) {
      setEligibleAffiliates(eligibleResult.data);
    }

    if (failedResult.success) {
      setFailedPayouts(failedResult.data);
    }

    setLoading(false);
  }, []);

  // Initial load
  useEffect(() => {
    loadDashboardData();
  }, [loadDashboardData]);

  // Calculate summary stats
  const summaryStats = useMemo(() => {
    return payoutDashboardService.getSummaryStats(dashboardData);
  }, [dashboardData]);

  // Sorting logic
  const handleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  // Sorted data
  const sortedData = useMemo(() => {
    if (!dashboardData.length) return [];

    const sorted = [...dashboardData].sort((a, b) => {
      let aVal = a[sortConfig.key];
      let bVal = b[sortConfig.key];

      // Handle special cases
      if (sortConfig.key === "has_wise_account") {
        aVal = aVal ? 1 : 0;
        bVal = bVal ? 1 : 0;
      } else if (sortConfig.key === "unpaid_balance" || sortConfig.key === "total_paid_out") {
        aVal = parseFloat(aVal) || 0;
        bVal = parseFloat(bVal) || 0;
      } else if (sortConfig.key === "last_payout_date") {
        aVal = aVal ? new Date(aVal).getTime() : 0;
        bVal = bVal ? new Date(bVal).getTime() : 0;
      } else if (typeof aVal === "string") {
        aVal = aVal?.toLowerCase() || "";
        bVal = bVal?.toLowerCase() || "";
      }

      if (aVal < bVal) return sortConfig.direction === "asc" ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === "asc" ? 1 : -1;
      return 0;
    });

    return sorted;
  }, [dashboardData, sortConfig]);

  // Render sortable header
  const SortableHeader = ({ label, sortKey, className = "" }) => (
    <th
      className={`text-left py-3 px-4 text-gray-400 font-medium cursor-pointer hover:bg-white/5 transition-colors ${className}`}
      onClick={() => handleSort(sortKey)}
    >
      <span className="flex items-center">
        {label}
        <SortIndicator
          columnKey={sortKey}
          currentSortConfig={sortConfig}
        />
      </span>
    </th>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 text-[#B8460E] animate-spin" />
        <span className="ml-3 text-gray-400">Loading dashboard data...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-white flex items-center gap-2">
            <Wallet className="h-6 w-6 text-[#B8460E]" />
            Payout Dashboard
          </h2>
          <p className="text-gray-400 mt-1">
            Overview of affiliate payment accounts and balances
          </p>
        </div>
        <button
          onClick={loadDashboardData}
          disabled={loading}
          className="flex items-center gap-2 px-4 py-2 bg-white/10 hover:bg-white/20 text-white rounded-lg transition-colors disabled:opacity-50"
        >
          <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </button>
      </div>

      {error && <Alert type="error" message={error} />}

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-400 mb-1">Wise Registered</p>
              <p className="text-2xl font-bold text-green-400">
                {summaryStats.registered}
              </p>
              <p className="text-xs text-gray-500 mt-1">affiliates</p>
            </div>
            <div className="p-3 bg-green-400/10 rounded-full">
              <CheckCircle className="h-6 w-6 text-green-400" />
            </div>
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-400 mb-1">Not Registered</p>
              <p className="text-2xl font-bold text-red-400">
                {summaryStats.notRegistered}
              </p>
              <p className="text-xs text-gray-500 mt-1">affiliates</p>
            </div>
            <div className="p-3 bg-red-400/10 rounded-full">
              <XCircle className="h-6 w-6 text-red-400" />
            </div>
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-400 mb-1">Total Owed</p>
              <p className="text-2xl font-bold text-yellow-400">
                {formatCurrency(summaryStats.totalOwed)}
              </p>
              <p className="text-xs text-gray-500 mt-1">unpaid balances</p>
            </div>
            <div className="p-3 bg-yellow-400/10 rounded-full">
              <DollarSign className="h-6 w-6 text-yellow-400" />
            </div>
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-400 mb-1">Total Paid Out</p>
              <p className="text-2xl font-bold text-purple-400">
                {formatCurrency(summaryStats.totalPaidOut)}
              </p>
              <p className="text-xs text-gray-500 mt-1">all time</p>
            </div>
            <div className="p-3 bg-purple-400/10 rounded-full">
              <DollarSign className="h-6 w-6 text-purple-400" />
            </div>
          </div>
        </Card>
      </div>

      {/* Week Finalization Status */}
      <Card className="p-6">
        <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
          <Clock className="h-5 w-5 text-[#B8460E]" />
          Week Finalization Status
        </h3>
        <p className="text-gray-400 text-sm mb-4">
          Auto-payouts trigger when a week is fully finalized (7/7 days). Weeks before Dec 29, 2025 are excluded.
        </p>

        {weekStatus.length === 0 ? (
          <p className="text-gray-500">No week data available</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {weekStatus.map((week) => {
              const weekDate = new Date(week.week_start);
              const weekLabel = weekDate.toLocaleDateString("en-US", {
                month: "short",
                day: "numeric",
              });
              const endDate = new Date(weekDate);
              endDate.setDate(endDate.getDate() + 6);
              const endLabel = endDate.toLocaleDateString("en-US", {
                month: "short",
                day: "numeric",
              });
              const progress = (week.days_finalized / 7) * 100;
              const isCutoffBlocked = weekDate < new Date("2025-12-29");

              return (
                <div
                  key={week.week_start}
                  className={`p-4 rounded-lg border ${
                    week.is_complete
                      ? week.was_auto_processed
                        ? "border-purple-400/30 bg-purple-400/5"
                        : "border-green-400/30 bg-green-400/5"
                      : "border-white/10 bg-white/5"
                  }`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-white font-medium">
                      {weekLabel} - {endLabel}
                    </span>
                    {week.is_complete ? (
                      week.was_auto_processed ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs bg-purple-400/20 text-purple-400">
                          <Zap className="h-3 w-3" />
                          Paid
                        </span>
                      ) : isCutoffBlocked ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs bg-gray-400/20 text-gray-400">
                          Pre-cutoff
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs bg-green-400/20 text-green-400">
                          <CheckCircle className="h-3 w-3" />
                          Ready
                        </span>
                      )
                    ) : (
                      <span className="text-xs text-gray-500">
                        {week.days_finalized}/7 days
                      </span>
                    )}
                  </div>

                  {/* Progress bar */}
                  <div className="w-full bg-white/10 rounded-full h-2">
                    <div
                      className={`h-2 rounded-full transition-all ${
                        week.is_complete
                          ? week.was_auto_processed
                            ? "bg-purple-400"
                            : "bg-green-400"
                          : "bg-yellow-400"
                      }`}
                      style={{ width: `${progress}%` }}
                    />
                  </div>

                  {week.was_auto_processed && week.auto_payout_amount && (
                    <p className="text-xs text-purple-400 mt-2">
                      {formatCurrency(week.auto_payout_amount)} paid automatically
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </Card>

      {/* Failed Payouts */}
      {failedPayouts.length > 0 && (
        <Card className="p-6 border-red-400/20">
          <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-red-400" />
            Failed Payouts ({failedPayouts.length})
          </h3>
          <p className="text-gray-400 text-sm mb-4">
            These payouts failed and will be automatically retried with exponential backoff (1h, 6h, 24h). After 3 retries, they require manual review.
          </p>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-white/10">
                  <th className="text-left py-3 px-4 text-gray-400 font-medium">Affiliate</th>
                  <th className="text-left py-3 px-4 text-gray-400 font-medium">Amount</th>
                  <th className="text-left py-3 px-4 text-gray-400 font-medium">Error</th>
                  <th className="text-left py-3 px-4 text-gray-400 font-medium">Retries</th>
                  <th className="text-left py-3 px-4 text-gray-400 font-medium">Status</th>
                </tr>
              </thead>
              <tbody>
                {failedPayouts.map((payout) => {
                  const retryCount = payout.payment_details?.retry_count || 0;
                  const needsReview = payout.payment_details?.needs_review || false;
                  const errorMessage = payout.payment_details?.error_message || payout.notes || "Unknown error";
                  const isAutoPayout = payout.payment_details?.auto_payout || false;

                  return (
                    <tr
                      key={payout.id}
                      className="border-b border-white/5 hover:bg-white/5"
                    >
                      <td className="py-4 px-4">
                        <span className="text-white font-medium">{payout.affiliate_name}</span>
                      </td>
                      <td className="py-4 px-4">
                        <span className="text-red-400 font-semibold">
                          {formatCurrency(payout.amount)}
                        </span>
                      </td>
                      <td className="py-4 px-4">
                        <span className="text-gray-300 text-sm max-w-xs truncate block" title={errorMessage}>
                          {errorMessage.length > 50 ? errorMessage.substring(0, 50) + "..." : errorMessage}
                        </span>
                      </td>
                      <td className="py-4 px-4">
                        <span className={`text-sm ${retryCount >= 3 ? "text-red-400" : "text-yellow-400"}`}>
                          {retryCount}/3
                        </span>
                      </td>
                      <td className="py-4 px-4">
                        {needsReview ? (
                          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-red-400/10 text-red-400 border border-red-400/20">
                            <AlertTriangle className="h-3.5 w-3.5" />
                            Needs Review
                          </span>
                        ) : isAutoPayout ? (
                          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-yellow-400/10 text-yellow-400 border border-yellow-400/20">
                            <Clock className="h-3.5 w-3.5" />
                            Auto-Retry
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-gray-400/10 text-gray-400 border border-gray-400/20">
                            Manual
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Auto-Payout Eligible Affiliates */}
      {eligibleAffiliates.length > 0 && (
        <Card className="p-6">
          <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <Zap className="h-5 w-5 text-purple-400" />
            Eligible for Auto-Payout ({eligibleAffiliates.length})
          </h3>
          <p className="text-gray-400 text-sm mb-4">
            These affiliates have Wise registered and a balance of $25 or more. They will be paid automatically when the next week completes.
          </p>
          <div className="flex flex-wrap gap-3">
            {eligibleAffiliates.map((affiliate) => (
              <div
                key={affiliate.affiliate_name}
                className="flex items-center gap-2 px-3 py-2 bg-purple-400/10 border border-purple-400/20 rounded-lg"
              >
                <span className="text-white font-medium">{affiliate.affiliate_name}</span>
                <span className="text-purple-400 font-semibold">
                  {formatCurrency(affiliate.total_unpaid)}
                </span>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Affiliate Table */}
      <Card className="p-6">
        <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
          <Users className="h-5 w-5 text-[#B8460E]" />
          All Affiliates ({dashboardData.length})
        </h3>

        {dashboardData.length === 0 ? (
          <div className="text-center py-12">
            <Users className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-400">No affiliate data found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-white/10">
                  <SortableHeader label="Affiliate" sortKey="affiliate_name" />
                  <SortableHeader label="Wise Status" sortKey="has_wise_account" />
                  <SortableHeader label="Unpaid Balance" sortKey="unpaid_balance" />
                  <SortableHeader label="Last Payout" sortKey="last_payout_date" />
                  <SortableHeader label="Total Paid Out" sortKey="total_paid_out" />
                </tr>
              </thead>
              <tbody>
                {sortedData.map((affiliate, index) => (
                  <tr
                    key={affiliate.affiliate_name || index}
                    className="border-b border-white/5 hover:bg-white/5"
                  >
                    {/* Affiliate Name */}
                    <td className="py-4 px-4">
                      <div className="flex items-center space-x-3">
                        <div className="p-2 bg-white/10 rounded-full">
                          <Users className="h-4 w-4 text-gray-400" />
                        </div>
                        <p className="text-white font-medium">
                          {affiliate.affiliate_name}
                        </p>
                      </div>
                    </td>

                    {/* Wise Status */}
                    <td className="py-4 px-4">
                      {affiliate.has_wise_account ? (
                        <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-green-400/10 text-green-400 border border-green-400/20">
                          <CheckCircle className="h-3.5 w-3.5" />
                          Registered
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-red-400/10 text-red-400 border border-red-400/20">
                          <XCircle className="h-3.5 w-3.5" />
                          Not Registered
                        </span>
                      )}
                    </td>

                    {/* Unpaid Balance */}
                    <td className="py-4 px-4">
                      <p className={`font-semibold ${
                        parseFloat(affiliate.unpaid_balance) > 0
                          ? "text-yellow-400"
                          : "text-gray-400"
                      }`}>
                        {formatCurrency(affiliate.unpaid_balance)}
                      </p>
                    </td>

                    {/* Last Payout Date */}
                    <td className="py-4 px-4">
                      <div className="flex items-center gap-2 text-gray-300">
                        <Calendar className="h-4 w-4 text-gray-500" />
                        {formatDate(affiliate.last_payout_date)}
                      </div>
                    </td>

                    {/* Total Paid Out */}
                    <td className="py-4 px-4">
                      <p className="text-purple-400 font-semibold">
                        {formatCurrency(affiliate.total_paid_out)}
                      </p>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
};

export default PayoutDashboard;
